/**
 * Chat API Service
 * 
 * Handles all chat-related API operations including message sending,
 * history retrieval, participant management, and unread count tracking.
 * Uses sender/receiver API model for admin communication.
 * 
 * @module ChatService
 * @version 1.0.0
 * @author Healthcare Development Team
 */

import { axiosInstance } from '../config/api';

/**
 * Chat service class providing all chat-related API operations
 * 
 * @class ChatService
 */
class ChatService {
  /**
   * Base API endpoint for chat operations
   * @private
   * @static
   * @readonly
   */
  static BASE_ENDPOINT = '/api/chat';

  /**
   * Retry configuration for network failures
   * @private
   * @static
   * @readonly
   */
  static RETRY_CONFIG = {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 5000,
    backoffFactor: 2
  };

  /**
   * Sends a message to admin using sender/receiver model
   * 
   * @static
   * @async
   * @param {number} sender_id - ID of the message sender
   * @param {string} sender_type - Type of sender ("doctor" or "front_desk")
   * @param {string} message - Message content
   * @returns {Promise<Object>} Sent message response
   * @throws {Error} When message sending fails or validation errors occur
   * 
   * @example
   * try {
   *   const response = await ChatService.sendMessageToAdmin(123, 'doctor', 'Hello admin');
   *   console.log('Message sent:', response);
   * } catch (error) {
   *   console.error('Failed to send message:', error.message);
   * }
   */
  static async sendMessageToAdmin(sender_id, sender_type, message) {
    // Validate required fields
    if (!sender_id || !sender_type || !message) {
      throw new Error('sender_id, sender_type, and message are required');
    }

    // Validate sender_type
    const validSenderTypes = ['doctor', 'front_desk'];
    if (!validSenderTypes.includes(sender_type)) {
      throw new Error('sender_type must be either "doctor" or "front_desk"');
    }

    // Sanitize message content
    const sanitizedMessage = message.trim();
    if (!sanitizedMessage) {
      throw new Error('Message content cannot be empty');
    }

    const payload = {
      sender_id: Number(sender_id),
      sender_type,
      receiver_id: 1, // Always admin
      receiver_type: 'admin',
      message: sanitizedMessage
    };

    return await this._executeWithRetry(async () => {
      const response = await axiosInstance.post(`${this.BASE_ENDPOINT}/send`, payload);
      this.validateApiResponse(response.data, 'sendMessageToAdmin');
      return response.data;
    }, 'sendMessageToAdmin');
  }

  /**
   * Sends a message between any two users
   * 
   * @static
   * @async
   * @param {number} sender_id - ID of the message sender
   * @param {string} sender_type - Type of sender ("doctor", "front_desk", or "admin")
   * @param {number} receiver_id - ID of the message receiver
   * @param {string} receiver_type - Type of receiver ("doctor", "front_desk", or "admin")
   * @param {string} message - Message content
   * @returns {Promise<Object>} Sent message response
   * 
   * @throws {Error} When message sending fails or validation errors occur
   * 
   * @example
   * try {
   *   const response = await ChatService.sendMessage(1, 'admin', 123, 'doctor', 'Hello doctor');
   *   console.log('Message sent:', response);
   * } catch (error) {
   *   console.error('Failed to send message:', error.message);
   * }
   */
  static async sendMessage(sender_id, sender_type, receiver_id, receiver_type, message) {
    // Validate required fields
    if (!sender_id || !sender_type || !receiver_id || !receiver_type || !message) {
      throw new Error('sender_id, sender_type, receiver_id, receiver_type, and message are required');
    }

    // Validate sender_type and receiver_type
    const validTypes = ['doctor', 'front_desk', 'admin'];
    if (!validTypes.includes(sender_type) || !validTypes.includes(receiver_type)) {
      throw new Error('sender_type and receiver_type must be one of: "doctor", "front_desk", "admin"');
    }

    // Sanitize message content
    const sanitizedMessage = message.trim();
    if (!sanitizedMessage) {
      throw new Error('Message content cannot be empty');
    }

    const payload = {
      sender_id: Number(sender_id),
      sender_type,
      receiver_id: Number(receiver_id),
      receiver_type,
      message: sanitizedMessage
    };

    return await this._executeWithRetry(async () => {
      const response = await axiosInstance.post(`${this.BASE_ENDPOINT}/send`, payload);
      this.validateApiResponse(response.data, 'sendMessage');
      return response.data;
    }, 'sendMessage');
  }

  /**
   * Retrieves message history between user and admin
   * 
   * @static
   * @async
   * @param {number} user1_id - First user ID (current user)
   * @param {string} user1_type - First user type ("doctor" or "front_desk")
   * @param {number} user2_id - Second user ID (admin, always 1)
   * @param {string} user2_type - Second user type (always "admin")
   * @returns {Promise<Array>} Array of message objects
   * 
   * @example
   * const history = await ChatService.getMessageHistory(123, 'doctor', 1, 'admin');
   * console.log('Messages:', history);
   */
  static async getMessageHistory(user1_id, user1_type, user2_id, user2_type) {
    // Validate required parameters
    if (!user1_id || !user1_type || !user2_id || !user2_type) {
      throw new Error('All parameters (user1_id, user1_type, user2_id, user2_type) are required');
    }

    const params = new URLSearchParams({
      user1_id: user1_id.toString(),
      user1_type,
      user2_id: user2_id.toString(),
      user2_type
    });

    return await this._executeWithRetry(async () => {
      const response = await axiosInstance.get(`${this.BASE_ENDPOINT}/history?${params}`);
      this.validateApiResponse(response.data, 'getMessageHistory');
      return response.data;
    }, 'getMessageHistory');
  }

  /**
   * Retrieves all participants who have messaged the admin
   * 
   * @static
   * @async
   * @param {number} user_id - Admin user ID (always 1)
   * @param {string} user_type - Admin user type (always "admin")
   * @returns {Promise<Array>} Array of participant objects
   * 
   * @example
   * const participants = await ChatService.getAdminParticipants(1, 'admin');
   * console.log('Participants:', participants);
   */
  static async getAdminParticipants(user_id = 1, user_type = 'admin') {
    const params = new URLSearchParams({
      user_id: user_id.toString(),
      user_type
    });

    return await this._executeWithRetry(async () => {
      const response = await axiosInstance.get(`${this.BASE_ENDPOINT}/participants?${params}`);
      this.validateApiResponse(response.data, 'getAdminParticipants');
      return response.data;
    }, 'getAdminParticipants');
  }

  /**
   * Retrieves unread message count for admin
   * 
   * @static
   * @async
   * @param {number} receiver_id - Admin user ID (always 1)
   * @param {string} receiver_type - Admin user type (always "admin")
   * @returns {Promise<Object>} Unread count information
   * 
   * @example
   * const unreadCount = await ChatService.getAdminUnreadCount(1, 'admin');
   * console.log('Unread messages:', unreadCount.unread_count);
   */
  static async getAdminUnreadCount(receiver_id = 1, receiver_type = 'admin') {
    const params = new URLSearchParams({
      receiver_id: receiver_id.toString(),
      receiver_type
    });

    return await this._executeWithRetry(async () => {
      const response = await axiosInstance.get(`${this.BASE_ENDPOINT}/unread-count?${params}`);
      this.validateApiResponse(response.data, 'getAdminUnreadCount');
      return response.data;
    }, 'getAdminUnreadCount');
  }

  /**
   * Extracts current user information from authentication context
   * 
   * @static
   * @returns {Object} User context information
   * @returns {number} returns.userId - User ID
   * @returns {string} returns.userType - User type ("doctor", "front_desk", or "admin")
   * @returns {string} returns.userName - User display name
   * @returns {string} returns.role - User role
   * @throws {Error} When user is not authenticated or user data is invalid
   * 
   * @example
   * try {
   *   const userInfo = ChatService.getCurrentUserInfo();
   *   console.log('Current user:', userInfo.userName, userInfo.userType);
   * } catch (error) {
   *   console.error('User not authenticated:', error.message);
   * }
   */
  static getCurrentUserInfo() {
    try {
      // Get user data from localStorage
      const userStr = localStorage.getItem('user');
      const tokenPayloadStr = localStorage.getItem('tokenPayload');
      
      if (!userStr) {
        throw new Error('User not authenticated - no user data found');
      }

      const userData = JSON.parse(userStr);
      const tokenPayload = tokenPayloadStr ? JSON.parse(tokenPayloadStr) : {};

      // Extract user ID from multiple possible sources
      const userId = userData.id || 
                    userData.user_id || 
                    userData.doctor_id || 
                    tokenPayload.userId || 
                    tokenPayload.doctor_id;

      if (!userId) {
        throw new Error('User ID not found in authentication data');
      }

      // Determine user type based on role information
      const role = (userData.role || 
                   userData.role_name || 
                   userData.user_type || 
                   userData.userRole || 
                   tokenPayload.role || 
                   '').toString().toLowerCase();

      let userType;
      if (role.includes('doctor') || role.includes('physician')) {
        userType = 'doctor';
      } else if (role.includes('front_desk') || role.includes('frontdesk') || role.includes('reception')) {
        userType = 'front_desk';
      } else if (role.includes('admin') || role.includes('administrator')) {
        userType = 'admin';
      } else {
        // Default to front_desk if role is unclear
        userType = 'front_desk';
      }

      // Extract user name from multiple possible sources
      const userName = userData.name || 
                      userData.doctor || 
                      userData.first_name || 
                      userData.username || 
                      tokenPayload.doctor || 
                      'User';

      return {
        userId: Number(userId),
        userType,
        userName,
        role: role || 'user'
      };
    } catch (error) {
      if (error.message.includes('User not authenticated')) {
        throw error;
      }
      throw new Error(`Failed to extract user information: ${error.message}`);
    }
  }

  /**
   * Formats message data for display in the UI
   * 
   * @static
   * @param {Object} messageData - Raw message data from API
   * @returns {Object} Formatted message object
   * @returns {number} returns.id - Message ID
   * @returns {string} returns.message - Message content
   * @returns {string} returns.senderName - Sender display name
   * @returns {string} returns.senderType - Sender type
   * @returns {Date} returns.timestamp - Message timestamp
   * @returns {boolean} returns.isRead - Whether message is read
   * @returns {boolean} returns.isFromCurrentUser - Whether message is from current user
   * 
   * @example
   * const rawMessage = { id: 1, message: 'Hello', sender_name: 'Dr. Smith', created_at: '2023-01-01T10:00:00Z' };
   * const formatted = ChatService.formatMessageForDisplay(rawMessage);
   * console.log(formatted.senderName, formatted.timestamp);
   */
  static formatMessageForDisplay(messageData) {
    if (!messageData) {
      throw new Error('Message data is required');
    }

    try {
      const currentUser = this.getCurrentUserInfo();
      
      return {
        id: messageData.id,
        message: messageData.message || '',
        senderName: messageData.sender_name || 'Unknown User',
        senderType: messageData.sender_type || 'unknown',
        receiverName: messageData.receiver_name || 'Unknown User',
        receiverType: messageData.receiver_type || 'unknown',
        timestamp: messageData.created_at ? new Date(messageData.created_at) : new Date(),
        isRead: Boolean(messageData.is_read),
        isFromCurrentUser: Number(messageData.sender_id) === currentUser.userId,
        senderId: messageData.sender_id,
        receiverId: messageData.receiver_id
      };
    } catch (error) {
      // If we can't get current user info, still format the message but without isFromCurrentUser
      return {
        id: messageData.id,
        message: messageData.message || '',
        senderName: messageData.sender_name || 'Unknown User',
        senderType: messageData.sender_type || 'unknown',
        receiverName: messageData.receiver_name || 'Unknown User',
        receiverType: messageData.receiver_type || 'unknown',
        timestamp: messageData.created_at ? new Date(messageData.created_at) : new Date(),
        isRead: Boolean(messageData.is_read),
        isFromCurrentUser: false,
        senderId: messageData.sender_id,
        receiverId: messageData.receiver_id
      };
    }
  }

  /**
   * Validates API response data structure
   * 
   * @static
   * @param {any} response - API response to validate
   * @param {string} operation - Operation name for error logging
   * @returns {boolean} Whether response is valid
   * @throws {Error} When response is invalid
   */
  static validateApiResponse(response, operation) {
    if (!response) {
      throw new Error(`${operation}: No response received from server`);
    }

    if (typeof response !== 'object') {
      throw new Error(`${operation}: Invalid response format - expected object`);
    }

    // Log successful validation in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ChatService] ${operation} - Response validation passed`);
    }

    return true;
  }

  /**
   * Executes API request with retry logic and exponential backoff
   * 
   * @private
   * @static
   * @async
   * @param {Function} requestFn - Function that returns a promise for the API request
   * @param {string} operation - Description of the operation for logging
   * @returns {Promise<any>} API response data
   * @throws {Error} When all retry attempts fail
   */
  static async _executeWithRetry(requestFn, operation) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.RETRY_CONFIG.maxAttempts; attempt++) {
      try {
        this._logRequest(operation, attempt);
        const result = await requestFn();
        this._logResponse(operation, attempt, true);
        return result;
      } catch (error) {
        lastError = error;
        this._logResponse(operation, attempt, false, error);
        
        // Don't retry on authentication or client errors (4xx)
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          break;
        }
        
        // Don't retry on the last attempt
        if (attempt === this.RETRY_CONFIG.maxAttempts) {
          break;
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          this.RETRY_CONFIG.baseDelay * Math.pow(this.RETRY_CONFIG.backoffFactor, attempt - 1),
          this.RETRY_CONFIG.maxDelay
        );
        
        console.warn(`Chat API retry ${attempt}/${this.RETRY_CONFIG.maxAttempts} for ${operation} in ${delay}ms`);
        await this._delay(delay);
      }
    }
    
    throw this._handleError(lastError);
  }

  /**
   * Creates a delay for retry logic
   * 
   * @private
   * @static
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  static _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Logs API request attempts for debugging
   * 
   * @private
   * @static
   * @param {string} operation - Operation description
   * @param {number} attempt - Attempt number
   */
  static _logRequest(operation, attempt) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ChatService] ${operation} - Attempt ${attempt}`);
    }
  }

  /**
   * Logs API response results for debugging
   * 
   * @private
   * @static
   * @param {string} operation - Operation description
   * @param {number} attempt - Attempt number
   * @param {boolean} success - Whether the request succeeded
   * @param {Error} [error] - Error object if request failed
   */
  static _logResponse(operation, attempt, success, error = null) {
    if (process.env.NODE_ENV === 'development') {
      if (success) {
        console.log(`[ChatService] ${operation} - Attempt ${attempt} succeeded`);
      } else {
        console.error(`[ChatService] ${operation} - Attempt ${attempt} failed:`, {
          status: error?.response?.status,
          message: error?.message,
          data: error?.response?.data
        });
      }
    }
  }



  /**
   * Handles API errors and provides consistent error formatting
   * Includes specific handling for different HTTP status codes
   * 
   * @private
   * @static
   * @param {Error} error - Original error object
   * @returns {Error} Formatted error with consistent message structure
   */
  static _handleError(error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      const errorData = error.response.data;
      
      // Log error details for debugging (avoid logging sensitive information)
      console.error(`[ChatService] API Error ${status}:`, {
        url: error.config?.url?.replace(/\/api\/chat\/.*/, '/api/chat/[REDACTED]'),
        method: error.config?.method,
        status,
        message: message?.substring(0, 100), // Limit message length
        hasData: !!errorData
      });
      
      switch (status) {
        case 400:
          // Bad Request - validation errors, malformed data
          return new Error(`Invalid request: ${message || 'Please check your input and try again'}`);
          
        case 401:
          // Unauthorized - authentication required or failed
          // Clear stored tokens and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tokenPayload');
          
          // Trigger redirect to login page
          if (typeof window !== 'undefined') {
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
          
          return new Error('Your session has expired. Please log in again.');
          
        case 403:
          // Forbidden - user doesn't have permission
          return new Error(`Access denied: ${message || 'You do not have permission to perform this action'}`);
          
        case 404:
          // Not Found - resource doesn't exist
          return new Error(`Not found: ${message || 'The requested resource was not found'}`);
          
        case 429:
          // Too Many Requests - rate limiting
          return new Error('Too many requests. Please wait a moment and try again.');
          
        case 500:
          // Internal Server Error
          return new Error('A server error occurred. Please try again later.');
          
        case 502:
        case 503:
        case 504:
          // Bad Gateway, Service Unavailable, Gateway Timeout
          return new Error('The service is temporarily unavailable. Please try again later.');
          
        default:
          return new Error(`An error occurred (${status}). Please try again.`);
      }
    }
    
    // Network errors, timeouts, etc.
    if (error.code === 'ECONNABORTED') {
      return new Error('The request timed out. Please check your connection and try again.');
    }
    
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      return new Error('Unable to connect to the server. Please check your internet connection.');
    }
    
    return new Error(`An unexpected error occurred: ${error.message || 'Please try again'}`);
  }
}

export default ChatService;